"use client";
import { SkeletonLoader } from "@/components/ui/skeleton";
import { amenitiesByCode, amenitiesByName } from "@/info/amenities";
import Image from "next/image";

interface AmenitiesSectionProps {
	amenities: string[];
	isLoading?: boolean;
}

// Define subsection mapping based on type field
const typeToSubsection = {
	basic: "Basic",
	games: "Sports/Recreational",
	sports: "Sports/Recreational",
	health: "Sports/Recreational",
	entertainment: "Community",
	lifestyle: "Lifestyle",
	kids: "Community",
	eco: "Eco-Friendly",
};

// Define subsection order
const subsectionOrder = ["Basic", "Sports/Recreational", "Community", "Lifestyle", "Eco-Friendly", "Others"];

export function AmenitiesSection({ amenities, isLoading = false }: AmenitiesSectionProps) {
	const hasAmenities = amenities?.length > 0;

	// Don't render anything if no amenities and not loading
	if (!hasAmenities && !isLoading) return null;

	// Group amenities by subsection
	const groupedAmenities = hasAmenities ? groupAmenitiesBySubsection(amenities) : {};

	return (
		<div>
			<div className="@4xl:hidden mt-3 space-y-4">
				{hasAmenities || !isLoading
					? subsectionOrder.map(subsection => {
							const amenitiesInSubsection = groupedAmenities[subsection];
							if (!amenitiesInSubsection || amenitiesInSubsection.length === 0) return null;
							return (
								<div key={subsection}>
									<h3 className="text-right text-base font-bold text-secondary-800 mb-2">{subsection}</h3>
									<div className="flex flex-wrap gap-2">
										{amenitiesInSubsection.map((amenity, index) => (
											<div
												key={index}
												className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-full"
											>
												<AmenityIcon amenity={amenity} />
												<span className="text-sm text-gray-900">{getAmenityNameAndIcon(amenity).name}</span>
											</div>
										))}
									</div>
								</div>
							);
						})
					: // Show skeleton amenities when loading
						Array.from({ length: 2 }).map((_, subsectionIndex) => (
							<div key={subsectionIndex}>
								<SkeletonLoader height="16px" width="120px" className="mb-2" />
								<div className="flex flex-wrap gap-2">
									{Array.from({ length: 3 }).map((_, index) => (
										<div key={index} className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-full">
											<SkeletonLoader height="16px" width="16px" className="rounded" />
											<SkeletonLoader height="16px" width="80px" />
										</div>
									))}
								</div>
							</div>
						))}
			</div>

			{/* Desktop Layout - UnitAmenities Style */}
			<div className="hidden @4xl:block mt-3">
				<div className="bg-white rounded-lg border border-gray-200 p-6 space-y-8">
					{hasAmenities || !isLoading
						? subsectionOrder.map(subsection => {
								const amenitiesInSubsection = groupedAmenities[subsection];
								if (!amenitiesInSubsection || amenitiesInSubsection.length === 0) return null;
								return (
									<div key={subsection}>
										<h3 className="text-right text-base font-bold text-secondary-800 mb-4 underline">{subsection}</h3>
										<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
											{amenitiesInSubsection.map((amenity, index) => (
												<div key={index} className="text-center">
													<div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-3">
														<AmenityIcon amenity={amenity} className="w-8 h-8" />
													</div>
													<p className="text-sm font-semibold text-gray-700 leading-tight">
														{getAmenityNameAndIcon(amenity).name}
													</p>
												</div>
											))}
										</div>
									</div>
								);
							})
						: // Show skeleton amenities when loading
							Array.from({ length: 2 }).map((_, subsectionIndex) => (
								<div key={subsectionIndex}>
									<SkeletonLoader height="16px" width="120px" className="mb-4" />
									<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
										{Array.from({ length: 3 }).map((_, index) => (
											<div key={index} className="text-center">
												<div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-3">
													<SkeletonLoader height="32px" width="32px" className="rounded" />
												</div>
												<SkeletonLoader height="16px" width="80px" />
											</div>
										))}
									</div>
								</div>
							))}
				</div>
			</div>
		</div>
	);
}

function groupAmenitiesBySubsection(amenities: string[]): Record<string, string[]> {
	const grouped: Record<string, string[]> = {};

	amenities.forEach(amenity => {
		const amenityInfo = amenitiesByCode.get(amenity) || amenitiesByName.get(amenity);
		// Use the first type to determine subsection
		const primaryType = amenityInfo?.type?.[0] || "";
		const subsection = typeToSubsection[primaryType as keyof typeof typeToSubsection] || "Others";

		if (!grouped[subsection]) {
			grouped[subsection] = [];
		}
		grouped[subsection].push(amenity);
	});

	return grouped;
}

function getAmenityNameAndIcon(amenity: string): { name: string; iconPath: string } {
	// First try to find in amenitiesInfo by code or name
	const amenityInfo = amenitiesByCode.get(amenity) || amenitiesByName.get(amenity);
	if (amenityInfo) {
		return {
			name: amenityInfo.name,
			iconPath: amenityInfo.icon,
		};
	}

	// Default fallback - use original amenity name and generic icon
	return {
		name: amenity.charAt(0).toUpperCase() + amenity.slice(1).toLowerCase(),
		iconPath: "amenities/colored/club-house",
	};
}

function AmenityIcon({ amenity, className = "w-4 h-4" }: { amenity: string; className?: string }) {
	const { iconPath } = getAmenityNameAndIcon(amenity);
	const [size] = className.match(/w-(\d+)/) || ["4"];
	const sizeNum = parseInt(size.replace("w-", "")) * 4; // Convert Tailwind size to pixels

	return (
		<div className={`${className} flex items-center justify-center`}>
			<Image
				src={`/images/${iconPath}.svg`}
				alt={amenity}
				width={sizeNum}
				height={sizeNum}
				className={`${className} object-contain text-primary`}
			/>
		</div>
	);
}
